import React, { useMemo } from "react";
import { Collapse, Popover } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { ContentDecision, Investigation, InvestigationMarker, MarkerStatus } from "../../../../../../types/healthcare/PatientAnalysisTypes";
import { Typography } from "antd";
import { fetchPatientBloodTests } from "../../../../../../api/healthcare/Testing/BloodTestApi";
import { BloodTest } from "../../../../../../types/lab/BloodTestTypes";
import { Test } from "../../../../../../types/healthcare/TestTypes";
import BloodResultsTable from "../../../../results/testOrderComponents/BloodResultsTable";
import DismissibleItem, { getContentDecision } from "./DismissibleItem";
import StatusTag from "../../../../../../components/shared/StatusTag";
import EmptyState from "../../../../../../components/shared/EmptyState";
import type { UserUuid } from "../../../../../../types/identity";

const { Text } = Typography;

interface Props {
  investigations: Investigation[];
  patientId: UserUuid;
  decisions: ContentDecision[];
  onChange: (itemKey: string, patch: Partial<ContentDecision>) => void;
  isLocked: boolean;
}

// Small, non-competing visual cues (spark dot, panel accent border) are colored
// across the full severity range, including a reassuring green for "normal" —
// they're quiet enough not to fight for attention. Marker VALUE text stays
// plain for "normal" (see STATUS_TEXT_COLOR below) so color reads as an alert,
// not decoration — only deviations get flagged.
const STATUS_ACCENT: Record<MarkerStatus, string> = {
  normal:     "var(--mg-success)",
  borderline: "var(--mg-warning)",
  abnormal:   "var(--mg-danger)",
  critical:   "var(--mg-danger)",
};

const STATUS_TEXT_COLOR: Record<MarkerStatus, string> = {
  normal:     "var(--mg-text)",
  borderline: "var(--mg-warning)",
  abnormal:   "var(--mg-danger)",
  critical:   "var(--mg-danger)",
};

const SparkBar: React.FC<{ marker: InvestigationMarker }> = ({ marker }) => {
  if (
    marker.numeric_value === null ||
    marker.reference_min === null ||
    marker.reference_max === null ||
    marker.reference_min === marker.reference_max
  ) {
    return null;
  }

  const range    = marker.reference_max - marker.reference_min;
  const pct      = Math.min(100, Math.max(0, ((marker.numeric_value - marker.reference_min) / range) * 100));
  const dotColor = STATUS_ACCENT[marker.status] ?? "var(--mg-text-secondary)";

  return (
    <div
      style={{
        position: "relative",
        height: 4,
        borderRadius: 2,
        background: "var(--mg-border)",
        width: 60,
        flexShrink: 0,
        alignSelf: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: `${pct}%`,
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: dotColor,
        }}
      />
    </div>
  );
};

const MarkerRow: React.FC<{ marker: InvestigationMarker }> = ({ marker }) => (
  <div
    style={{
      display:      "flex",
      alignItems:   "center",
      gap:          8,
      padding:      "4px 0",
      borderBottom: "1px solid var(--mg-border)",
    }}
  >
    <Text
      style={{ fontSize: 11, flex: 2, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      title={marker.test_name}
    >
      {marker.test_name}
    </Text>
    <Text
      style={{
        fontSize:   11,
        fontWeight: 600,
        color:      STATUS_TEXT_COLOR[marker.status] ?? "var(--mg-text)",
        flexShrink: 0,
        width:      70,
        textAlign:  "right",
      }}
    >
      {marker.value}
    </Text>
    <Text style={{ fontSize: 10, color: "var(--mg-text-secondary)", flexShrink: 0, width: 50 }}>
      {marker.unit}
    </Text>
    <SparkBar marker={marker} />
    {marker.clinical_significance && (
      <Popover
        content={
          <Text style={{ fontSize: 12, maxWidth: 260, display: "block" }}>
            {marker.clinical_significance}
          </Text>
        }
        trigger="hover"
        placement="top"
      >
        <InfoCircleOutlined
          style={{ fontSize: 12, color: "var(--mg-text-secondary)", flexShrink: 0, cursor: "help" }}
        />
      </Popover>
    )}
  </div>
);

const InvestigationsTab: React.FC<Props> = ({ investigations, patientId, decisions, onChange, isLocked }) => {
  // Blood-work investigations carry only an AI summary; the detailed markers are
  // rendered from the live blood results, keyed by the real test order id.
  const { data: bloodTests } = useQuery({
    queryKey: ["patient-blood-tests", patientId],
    queryFn: () => fetchPatientBloodTests(patientId),
    enabled: !!patientId,
  });

  const bloodOrderById = useMemo(() => {
    const map = new Map<string, Partial<Test>>();
    (bloodTests ?? []).forEach((bt: BloodTest) => {
      if (bt.test?.id != null) {
        map.set(String(bt.test.id), { ...bt.test, blood_test_id: bt.id } as unknown as Partial<Test>);
      }
    });
    return map;
  }, [bloodTests]);

  // Defensive: the analysis output can occasionally arrive as a non-array (e.g. an
  // empty keyed object), and genetics findings belong in the dedicated Genetic tab.
  const items = (Array.isArray(investigations) ? investigations : []).filter(
    (inv) => (inv.order_type as string) !== "genetic",
  );

  if (items.length === 0) {
    return (
      <EmptyState
        title="No investigation results"
        description="Investigation results will appear here once lab or blood work orders are completed and analysed."
      />
    );
  }

  const criticalCount   = items.filter((i) => i.overall_status === "critical").length;
  const abnormalCount   = items.filter((i) => i.overall_status === "abnormal").length;
  const normalCount     = items.filter((i) => i.overall_status === "normal").length;
  const borderlineCount = items.filter((i) => i.overall_status === "borderline").length;

  // Dismiss is applied at the card level (whole investigation, not per-marker); a
  // signed-off + dismissed investigation is dropped from the list entirely, matching
  // DismissibleItem's own "dismissed + locked → gone" rule.
  const collapseItems = items
    .filter((inv) => !(getContentDecision(decisions, `investigation:${inv.test_order_id}`)?.dismissed && isLocked))
    .map((inv, idx) => {
      const itemKey = `investigation:${inv.test_order_id}`;
      const dismissed = getContentDecision(decisions, itemKey)?.dismissed ?? false;
      const accentColor = STATUS_ACCENT[inv.overall_status] ?? "var(--mg-border)";
      return {
        key: String(idx),
        // Only the expand icon toggles the panel — otherwise clicking the dismiss button
        // (rendered inside the label by DismissibleItem) would also bubble up and toggle it.
        collapsible: "icon" as const,
        // Left-border severity accent so status is scannable across a collapsed list
        // without reading every tag.
        style: { borderLeft: `3px solid ${accentColor}` },
        label: (
          <DismissibleItem itemKey={itemKey} decisions={decisions} onChange={onChange} isLocked={isLocked}>
            {() => (
              <div style={{ display: "flex", flexDirection: "column" as const, gap: 2, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <StatusTag status={inv.overall_status} size="small" />
                  <Text style={{ fontSize: 12, fontWeight: 600 }}>{inv.test_order_name}</Text>
                  <Text style={{ fontSize: 10, color: "var(--mg-text-secondary)", marginLeft: "auto", flexShrink: 0 }}>
                    {inv.test_date}
                    {inv.lab_name ? ` · ${inv.lab_name}` : ""}
                  </Text>
                </div>
                {inv.critical_summary && (
                  <Text style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>{inv.critical_summary}</Text>
                )}
              </div>
            )}
          </DismissibleItem>
        ),
        children: dismissed ? null : renderInvestigationBody(inv),
      };
    });

  function renderInvestigationBody(inv: Investigation) {
    // Blood work: render the live, structured blood results via the shared accordion.
    if (inv.order_type === "blood_work") {
      const order = bloodOrderById.get(inv.test_order_id);
      if (!order) {
        return (
          <Text style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>
            Detailed blood results are not available to display.
          </Text>
        );
      }
      return <BloodResultsTable currentOrder={order} mode="report" />;
    }

    // Other lab orders: keep the existing AI-generated marker table.
    return (
      <div style={{ display: "flex", flexDirection: "column" as const, gap: 8 }}>
        {(inv.result_groups ?? []).map((group, gi) => (
          <div key={gi}>
            {group.panel_category && (
              <Text
                style={{
                  fontSize:      10,
                  fontWeight:    600,
                  textTransform: "uppercase" as const,
                  letterSpacing: "0.05em",
                  color:         "var(--mg-text-secondary)",
                  display:       "block",
                  marginBottom:  4,
                }}
              >
                {group.panel_category}
              </Text>
            )}
            {group.markers.map((marker, mi) => (
              <MarkerRow key={mi} marker={marker} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Collapse items={collapseItems} size="small" />
    </div>
  );
};

export default InvestigationsTab;
